import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { ErrorPage } from '../components/ui/ErrorPage'
import { ApiError } from '../services/api'

interface ErrorContextType {
 setError: (error: Error | null) => void
 clearError: () => void
 hasError: boolean
 reportApiError: (error: unknown) => void
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined)

export const useErrorHandler = () => {
 const context = useContext(ErrorContext)
 if (!context) {
 throw new Error('useErrorHandler must be used within ErrorProvider')
 }
 return context
}

interface ErrorProviderProps {
 children: ReactNode
}

const isCriticalError = (error: Error): boolean => {
 // Erros de API com status 500+
 if (error instanceof ApiError) {
 return error.status !== undefined && error.status >= 500
 }
 
 // Erros de rede
 if (error.message.includes('Network') || 
 error.message.includes('fetch') || 
 error.message.includes('Failed to fetch') ||
 error.message.includes('NetworkError')) {
 return true
 }
 
 // Erros de timeout
 if (error.message.includes('timeout') || error.message.includes('Timeout')) {
 return true
 }
 
 return false
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
 const [error, setErrorState] = useState<Error | null>(null)

 const setError = useCallback((err: Error | null) => {
 if (err && isCriticalError(err)) {
 setErrorState(err)
 }
 }, [])

 const clearError = useCallback(() => {
 setErrorState(null)
 }, [])

 const reportApiError = useCallback((err: unknown) => {
 if (err instanceof ApiError || err instanceof Error) {
 if (isCriticalError(err)) {
 setErrorState(err)
 }
 } else if (err && typeof err === 'object' && 'message' in err) {
 const error = new Error(String(err.message))
 if (isCriticalError(error)) {
 setErrorState(error)
 }
 }
 }, [])

 if (error) {
 try {
 return (
 <ErrorPage
 error={error}
 onReset={clearError}
 showDetails={import.meta.env.DEV}
 />
 )
 } catch (renderError) {
 // Fallback caso o ErrorPage também falhe
 console.error('Erro ao renderizar ErrorPage no ErrorProvider:', renderError)
 return (
 <div style={{
 minHeight: '100vh',
 display: 'flex',
 alignItems: 'center',
 justifyContent: 'center',
 backgroundColor: '#f3f4f6',
 padding: '20px',
 fontFamily: 'system-ui, -apple-system, sans-serif'
 }}>
 <div style={{
 maxWidth: '600px',
 width: '100%',
 backgroundColor: 'white',
 borderRadius: '8px',
 padding: '32px',
 boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
 textAlign: 'center'
 }}>
 <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px', color: '#111827' }}>
 Erro Crítico
 </h1>
 <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '24px' }}>
 {error.message || 'Ocorreu um erro inesperado. Por favor, recarregue a página.'}
 </p>
 <button
 onClick={() => {
 clearError()
 window.location.reload()
 }}
 style={{
 backgroundColor: '#3b82f6',
 color: 'white',
 border: 'none',
 borderRadius: '6px',
 padding: '10px 20px',
 fontSize: '14px',
 fontWeight: '500',
 cursor: 'pointer'
 }}
 >
 Recarregar Página
 </button>
 </div>
 </div>
 )
 }
 }

 return (
 <ErrorContext.Provider value={{ setError, clearError, hasError: !!error, reportApiError }}>
 {children}
 </ErrorContext.Provider>
 )
}

// Hook para capturar erros de API
export const useApiErrorHandler = () => {
 const { reportApiError } = useErrorHandler()

 return { reportApiError }
}

